/**
 * A evolução da área de chuva na ficha: a área e as taxas máxima e média
 * (mm/h), empilhadas sobre um eixo de tempo só, com o cursor em comum — as
 * mesmas peças do gráfico do rastreio de nuvens (`tracking/TrackingChart`).
 */
import { useState } from 'react';
import { formatDecimal, formatInt } from '../lib/format';
import { formatTimeBrt, MINUTE_MS } from '../lib/time';
import { EixoTempo } from '../ui/GraficoTempo';
import { COR_AREA, Faixa, fatiaDoInstante, Linhas, minMax, Valor, type Cursor } from '../tracking/TrackingChart';
import type { HistoricoChuva } from './store';

export const COR_PMAX = '#ff7ac8';
export const COR_PMED = '#7fb8ff';

export interface SerieChuva {
  /** Instante de cada fatia (meio da hora, epoch ms), de 30 em 30 min; o último é o quadro. */
  instantes: number[];
  km2: (number | null)[];
  pmax: (number | null)[];
  pmed: (number | null)[];
}

/** A história em fatias de 30 min: horário que faltou vira buraco (`null`). `null` sem dois pontos. */
export function serieDaChuva(hist: HistoricoChuva | null, t: number, passoMin = 30): SerieChuva | null {
  if (!hist || hist.dt.length < 2) return null;
  const n = Math.round(-hist.dt[0]! / passoMin) + 1;
  if (n < 2 || n > 200) return null;
  const vazio = () => Array.from<number | null>({ length: n }).fill(null);
  const out: SerieChuva = { instantes: Array.from({ length: n }, (_, i) => t - (n - 1 - i) * passoMin * MINUTE_MS), km2: vazio(), pmax: vazio(), pmed: vazio() };
  hist.dt.forEach((dt, k) => {
    const i = n - 1 + Math.round(dt / passoMin);
    if (i < 0 || i >= n) return;
    out.km2[i] = hist.km2[k] ?? null;
    out.pmax[i] = hist.pmax[k] ?? null;
    out.pmed[i] = hist.pmed[k] ?? null;
  });
  return out;
}

const km2 = (v: number) => formatInt(v);
const mmh = (v: number) => formatDecimal(v, v < 10 ? 1 : 0);

export function EvolucaoDaChuva(props: { serie: SerieChuva; instante: number | null }) {
  const { serie } = props;
  const [sob, setSob] = useState<number | null>(null);
  const n = serie.instantes.length;
  const iAtual = fatiaDoInstante(serie.instantes, props.instante);
  const cursor: Cursor = { iAtual, sob, aoSob: setSob };
  const i = sob ?? (iAtual >= 0 ? iAtual : n - 1);
  const desde = formatTimeBrt(serie.instantes[0]!);
  const ate = formatTimeBrt(serie.instantes[n - 1]!);
  return (
    <div data-evolucao-chuva="">
      <Faixa titulo="Área (km²)" escala={minMax(km2, serie.km2)} leitura={<Valor v={serie.km2[i]} formato={km2} cor={COR_AREA} />}>
        <Linhas
          linhas={[{ serie: serie.km2, cor: COR_AREA, cheia: true }]}
          instantes={serie.instantes}
          piso={1}
          altura={40}
          cursor={cursor}
          rotulo={`Área da chuva das ${desde} às ${ate}, entre ${minMax(km2, serie.km2) ?? '—'} km²`}
        />
      </Faixa>
      <Faixa
        titulo={
          <>
            Chuva (mm/h): <span style={{ color: COR_PMAX }}>máx.</span> e <span style={{ color: COR_PMED }}>média</span>
          </>
        }
        leitura={
          <>
            <Valor v={serie.pmax[i]} formato={mmh} cor={COR_PMAX} /> <span className="text-ink-500">/</span> <Valor v={serie.pmed[i]} formato={mmh} cor={COR_PMED} />
          </>
        }
      >
        <Linhas
          linhas={[
            { serie: serie.pmax, cor: COR_PMAX },
            { serie: serie.pmed, cor: COR_PMED },
          ]}
          instantes={serie.instantes}
          piso={1}
          altura={44}
          cursor={cursor}
          rotulo={`Taxa de chuva máxima (${minMax(mmh, serie.pmax) ?? '—'} mm/h) e média (${minMax(mmh, serie.pmed) ?? '—'} mm/h) das ${desde} às ${ate}`}
        />
      </Faixa>
      <EixoTempo instantes={serie.instantes} perto={sob} />
      <div className="mt-0.5 text-[10px] text-ink-400 tabular-nums">
        {sob !== null ? (
          <span className="text-ink-200">{formatTimeBrt(serie.instantes[sob]!)}</span>
        ) : iAtual >= 0 ? (
          `hora das ${formatTimeBrt(serie.instantes[iAtual]! - 30 * MINUTE_MS)}–${formatTimeBrt(serie.instantes[iAtual]! + 30 * MINUTE_MS)}`
        ) : (
          `a imagem exibida está fora desta história (até ${ate})`
        )}
      </div>
    </div>
  );
}
